/**
 * The two sentences at the top of the Overview screen, its banners, and the lines
 * about an account's funding instruments on each card.
 *
 * Out of `Overview.tsx` because `node --test` refuses a `.tsx`: anything left in
 * the component is beyond the reach of anything that runs, and these strings are
 * what an operator reads before lending an ad account.
 *
 * `LoadState` is written out rather than imported from `useLoad.ts`. The type
 * would be the tidier import, and it would drag `api.ts` in behind it, which
 * reads `import.meta.env` through `config.ts` — and then this module would
 * typecheck, build, and fail to load under the runner.
 *
 * Nothing here decides anything. It states what was counted, and its one duty is
 * to never state more than was counted. It says nothing about an account's
 * spending limit or spend except the two sentences about funding instruments and
 * the instrument lines, which carry X's own figures: any other sentence about
 * money would be said about every account.
 *
 * A file of the same shape lives in ttads. The platform constants, the comments
 * about them and everything about funding instruments are this console's own and
 * are not expected to match byte for byte; compare before carrying either way.
 */
import { microMoney } from "./money.ts";

/** The platform, as an operator names it. */
const PLATFORM = "X";

/**
 * 🔴 Up to how many visible accounts this screen reads funding instruments for —
 * one `GET /api/v1/funding-instruments` per account, each a live call to X from a
 * landing page. Set 2026-10-20 with one account in `X_AD_ACCOUNT_IDS`; the number
 * is pinned by a case so that the landing page cannot become N calls to X in
 * silence. Above it the cards say where the instruments are read instead.
 */
export const INSTRUMENTS_READ_UP_TO = 10;

/** Whether this screen reads instruments for this many visible accounts. */
export function readsInstruments(accounts: number): boolean {
	return accounts > 0 && accounts <= INSTRUMENTS_READ_UP_TO;
}

/**
 * What the subhead says about money when the cards carry the instruments. X's
 * account object carries no spending limit; the nearest figures are on its funding
 * instruments (https://docs.x.com/x-ads-api/campaign-management/reference, read
 * 2026-09-14). Until 2026-10-20 the sentence here said the console does not read
 * them (false since twads#14, the Campaigns screen reads them for its create form),
 * then that it shows no limit from them; since the cards below show the credit
 * limit X holds on each instrument, it says that.
 */
const INSTRUMENTS_SHOWN =
	"Each card reads the account's funding instruments live from X: the instrument, whether it can fund, and the credit limit X holds on it.";

/** Over the limit above: both numbers, and where the instruments are read instead. */
function instrumentsNotRead(count: number): string {
	return `${count} accounts is more than the ${INSTRUMENTS_READ_UP_TO} this screen reads funding instruments for; they are read on Campaigns, not here.`;
}

export type LoadState = "loading" | "ready" | "failed";

/** One banner, ready to render. The screen decides nothing about it. */
export interface OverviewBanner {
	kind: "warning" | "danger";
	title: string;
	body: string;
	/** Where the banner points, when it points somewhere: a card on this screen. */
	link?: { text: string; href: string };
}

/**
 * Everything the Overview screen says. No counts and no flags leave this
 * module — only finished sentences and a list of banners to render in the order
 * given.
 */
export interface OverviewCopy {
	headline: string;
	subhead: string;
	banners: OverviewBanner[];
}

/**
 * One call, and the screen renders what comes back.
 *
 * `count` is the caller's share of the accounts; `truncated` is whether the read
 * behind it was cut off. Both can be true at once — a tenant whose grants sit
 * past the ceiling sees none of their own.
 */
export function overviewCopy(
	state: LoadState,
	count: number,
	truncated: boolean,
	/**
	 * When `state` is failed: the status the console answered with, or null when nothing
	 * answered (lib/api-error.ts). Decides the headline — a 403 is not an outage.
	 */
	failedStatus: number | null = null,
	/**
	 * When `state` is failed: the worker's `error`, as useLoad keeps it in `why`. A 503 is
	 * the sign-in check only when it says `auth_unavailable`; a 503 from elsewhere (a
	 * proxy's page, not JSON) is named by its status like any other.
	 */
	failedWhy: string | null = null,
): OverviewCopy {
	const banners: OverviewBanner[] = [];
	/*
	 * Only on a finished read. The banner says the numbers on screen are floors of
	 * a read that was cut off; while loading, or after a failure, there is no read
	 * and no numbers, so a floor there would be a claim about nothing. The screen
	 * passes `ready && truncated` today, and this holds without relying on it.
	 */
	if (state === "ready" && truncated)
		banners.push({
			kind: "warning",
			title: "These numbers are floors, not totals.",
			body: "More accounts exist than one read returns, so the count above and the cards below are drawn from the first 1000 by name. Nothing is missing from the database.",
		});
	return {
		headline: headline(state, count, truncated, failedStatus, failedWhy),
		subhead: subhead(state, count, truncated, failedStatus, failedWhy),
		banners,
	};
}

/*
 * 🔴 The failed headline goes by the status. Until 2026-10-22 it said «The console
 * database is unreachable.» on every failure, a 403 included (spa-review.md item 8).
 * On GET /x-accounts a 503 is the kit's `auth_unavailable` (middleware/auth.js): the
 * sign-in check, not the store. The route answers 500 for a database it cannot reach
 * (`failure()` in routes/v1.ts), so a database sentence belongs here only once that
 * route answers 503 for the kit's StoreUnavailableError. A 403 is a refusal; no answer
 * at all is the console.
 */
function headline(
	state: LoadState,
	count: number,
	truncated: boolean,
	failedStatus: number | null,
	failedWhy: string | null,
): string {
	if (state === "loading") return "Loading your accounts…";
	if (state === "failed") {
		if (signInDown(failedStatus, failedWhy)) return "The console could not check this sign-in.";
		if (failedStatus === 403) return "This sign-in may not read the ad accounts.";
		if (failedStatus === null) return "The console did not answer.";
		return `The ad accounts could not be read: the console answered ${failedStatus}.`;
	}
	// "At least 0" is not a sentence.
	if (truncated)
		return count === 0
			? "No ad accounts among the ones we could read."
			: `At least ${count} ad accounts under your control.`;
	if (count === 0) return "No ad accounts yet.";
	return count === 1
		? "One ad account under your control."
		: `${count} ad accounts under your control.`;
}

/**
 * The kit's `auth_unavailable` (middleware/auth.js: `{ error: "auth_unavailable" }`, 503),
 * and only that. 🔵 from twads#179's review: on the status alone, a 503 the edge answers
 * with a page of its own read as the sign-in check too.
 */
function signInDown(failedStatus: number | null, failedWhy: string | null): boolean {
	return failedStatus === 503 && failedWhy === "auth_unavailable";
}

function subhead(
	state: LoadState,
	count: number,
	truncated: boolean,
	failedStatus: number | null,
	failedWhy: string | null,
): string {
	if (state === "loading") return "Reading the accounts you have been granted.";
	if (state === "failed") {
		if (signInDown(failedStatus, failedWhy))
			return "Its sign-in check answered 503, which is not a refusal: nothing lists until the check is back. The console's reason is below.";
		if (failedStatus === 403)
			return "The console refused the read (403). What this sign-in may hold is set on Access.";
		if (failedStatus === null)
			return "Nothing came back — the network, or the console itself. The reason is below.";
		return "Nothing lists until the read succeeds. The console's reason is below.";
	}
	if (truncated)
		// The caller's share can be empty while the read was still cut off. Saying
		// "at least 0" there would be a sentence about nothing.
		return count === 0
			? "The read stopped at 1000 rows before reaching any of yours, which is not the same as having none."
			: "Not all of them were read: one read stops at 1000 rows.";
	if (count === 0)
		return `Sync the ad accounts from ${PLATFORM} on the Ad accounts page, then grant them out on Access.`;
	/*
	 * Said rather than left out. An operator who has seen a limit on another
	 * console would read silence as "no limit" — which is a claim about the
	 * account, not about this console.
	 */
	return readsInstruments(count) ? INSTRUMENTS_SHOWN : instrumentsNotRead(count);
}

/** The fields of a funding instrument these lines read; the contract's row has them all. */
export interface InstrumentFields {
	id: string;
	description: string | null;
	ableToFund: boolean;
	reasonsNotAbleToFund: readonly string[];
	creditLimitLocalMicro: number | null;
	currency: string | null;
}

/** One account's read of its instruments, as the screen holds it. */
export type InstrumentRead =
	| { state: "loading" }
	| { state: "failed"; why: string }
	| { state: "ready"; instruments: readonly InstrumentFields[] }
	/** Not read: more accounts on screen than `INSTRUMENTS_READ_UP_TO`. */
	| { state: "skipped"; accounts: number };

/** One rendered line, keyed for the list — the instrument's id, or the state's name. */
export interface InstrumentLine {
	key: string;
	text: string;
}

/**
 * The lines under an account card about its funding instruments.
 *
 * 🔴 Finished sentences, one per instrument, and nothing decided here: the
 * description is X's (on 2026-10-20 «(no payment method has been set up yet)» —
 * which is the state the owner looked for and could not find on this screen,
 * audits/2026-10-20/account-check.txt), `ableToFund` is X's verdict with X's
 * reasons when it is false, and the credit limit is X's figure through
 * `lib/money.ts` with the instrument's own currency — the only place in this SPA
 * that turns micro units into an amount. A null limit prints «—», never 0.
 */
export function instrumentLines(read: InstrumentRead): InstrumentLine[] {
	switch (read.state) {
		case "loading":
			return [{ key: "loading", text: "Reading funding instruments…" }];
		case "failed":
			return [{ key: "failed", text: `Funding instruments could not be read: ${read.why}` }];
		case "skipped":
			return [{ key: "skipped", text: instrumentsNotRead(read.accounts) }];
		case "ready":
			if (read.instruments.length === 0)
				return [{ key: "none", text: "X lists no funding instrument on this account." }];
			return read.instruments.map((f) => ({ key: f.id, text: instrumentLine(f) }));
	}
}

function instrumentLine(f: InstrumentFields): string {
	const name = f.description ?? f.id;
	const fund = f.ableToFund
		? "can fund"
		: f.reasonsNotAbleToFund.length > 0
			? `cannot fund: ${f.reasonsNotAbleToFund.join(", ")}`
			: "cannot fund, and X gave no reason";
	const limit =
		f.creditLimitLocalMicro === null ? "—" : microMoney(f.creditLimitLocalMicro, f.currency);
	return `${name} · ${fund} · credit limit ${limit}`;
}

// ─── signals: X's words, as the cycle kept them ──────────────────────────────

/** The fields of a signal row these lines read; the contract's row has them all. */
export interface SignalFields {
	entity: string;
	entityId: string;
	kind: string;
	value: string;
}

/** The fields of the signals answer these lines read. */
export interface SignalsFields {
	signals: readonly SignalFields[];
	stale: boolean;
	unavailable?: string;
	cycle: { okAt?: string | null } | null;
}

/** One account's read of its signals, as the screen holds it. */
export type SignalsRead =
	| { state: "loading" }
	| { state: "failed"; why: string }
	| { state: "ready"; answer: SignalsFields }
	/** Not read: more accounts on screen than `INSTRUMENTS_READ_UP_TO`. */
	| { state: "skipped"; accounts: number };

export type SignalLine = InstrumentLine;

/**
 * The interval of the cycle, twice — what `stale` means, in minutes. The cycle's
 * interval is the worker's `CYCLE_SECONDS` (lib/signals.ts); a case reads that file
 * and holds this number to twice it, since the SPA cannot import the worker.
 */
export const STALE_AFTER_MINUTES = 30;
const STALE_AFTER = `${STALE_AFTER_MINUTES} minutes`;

/** Where this screen's signals come from, said once. */
const SIGNALS_ARE = "X's words at the last read; this console decides nothing from them.";

/** The id of the card an account renders under, for the banner's link. */
export function accountCardId(xAccountId: string): string {
	return `account-${xAccountId}`;
}

const ENTITY_WORD: Record<string, string> = {
	account: "account",
	funding_instrument: "funding instrument",
	campaign: "campaign",
	promoted_tweet: "promoted tweet",
};

const KIND_WORD: Record<string, string> = {
	approval_status: "approval status",
	reason_not_servable: "not servable",
	servable: "servable",
	reason_not_able_to_fund: "cannot fund",
	able_to_fund: "able to fund",
};

function signalsNotRead(count: number): string {
	return `${count} accounts is more than the ${INSTRUMENTS_READ_UP_TO} this screen reads signals for; they are not read here.`;
}

function clock(iso: string | null | undefined): string {
	if (!iso) return "no read yet";
	const t = Date.parse(iso);
	return Number.isNaN(t)
		? "no read yet"
		: // Date and time: a stale read may be from another day; «22:15» alone would not say.
			`last read ${new Date(t).toISOString().slice(0, 16).replace("T", " ")} UTC`;
}

/**
 * The lines under an account card about its signals.
 *
 * 🔴 Finished sentences, one per (object, field), carrying X's words joined as X
 * listed them — «campaign phbsq · not servable: ACCOUNT_UNDER_REVIEW, STARTS_IN_FUTURE»
 * — and nothing decided here. A stale cycle and a missing migration are said as what
 * they are, before any word, so an empty list under either never reads as «no signal».
 */
export function signalLines(read: SignalsRead): SignalLine[] {
	switch (read.state) {
		case "loading":
			return [{ key: "loading", text: "Reading signals…" }];
		case "failed":
			return [{ key: "failed", text: `Signals could not be read: ${read.why}` }];
		case "skipped":
			return [{ key: "skipped", text: signalsNotRead(read.accounts) }];
		case "ready": {
			const a = read.answer;
			if (a.unavailable) return [{ key: "unavailable", text: `Signals: ${a.unavailable}.` }];
			const out: SignalLine[] = [];
			if (a.stale)
				out.push({
					key: "stale",
					text: `Signals are stale: no cycle has read X in the last ${STALE_AFTER} (${clock(a.cycle?.okAt)}).`,
				});
			if (a.signals.length === 0) {
				out.push({
					key: "none",
					text: `No signal from X on this account (${clock(a.cycle?.okAt)}).`,
				});
				return out;
			}
			const grouped = new Map<string, { head: string; values: string[] }>();
			for (const s of a.signals) {
				const key = `${s.entity}:${s.entityId}:${s.kind}`;
				const held = grouped.get(key);
				if (held) held.values.push(s.value);
				else
					grouped.set(key, {
						head: `${ENTITY_WORD[s.entity] ?? s.entity} ${s.entityId} · ${KIND_WORD[s.kind] ?? s.kind}`,
						values: [s.value],
					});
			}
			for (const [key, g] of grouped) out.push({ key, text: `${g.head}: ${g.values.join(", ")}` });
			return out;
		}
	}
}

/**
 * The banner above the cards: how many words X has about how many accounts, with a
 * link to the first card that carries one; or that the cycle is stale, when it is and
 * there is no word to count. Nothing while the reads are loading, failed, skipped or
 * unavailable — the cards say those, each for itself.
 */
export function signalsBanner(
	reads: Readonly<Record<string, SignalsRead>>,
	accounts: readonly { xAccountId: string; name: string }[],
): OverviewBanner | null {
	let total = 0;
	let withSignals = 0;
	let stale = false;
	let first: { xAccountId: string; name: string } | null = null;
	for (const a of accounts) {
		const r = reads[a.xAccountId];
		if (!r || r.state !== "ready" || r.answer.unavailable) continue;
		if (r.answer.stale) stale = true;
		if (r.answer.signals.length > 0) {
			total += r.answer.signals.length;
			withSignals++;
			first ??= a;
		}
	}
	if (total > 0 && first) {
		const n = (count: number, word: string) => `${count} ${word}${count === 1 ? "" : "s"}`;
		return {
			kind: "warning",
			title: `${n(total, "signal")} from X on ${n(withSignals, "account")}.`,
			body: `${SIGNALS_ARE}${stale ? ` The cycle is stale: no read in the last ${STALE_AFTER}.` : ""} The cards below carry each word.`,
			link: { text: `Go to ${first.name}`, href: `#${accountCardId(first.xAccountId)}` },
		};
	}
	if (stale)
		return {
			kind: "warning",
			title: "Signals are stale.",
			body: `No cycle has read X in the last ${STALE_AFTER}; the cards below show the last read, which may be old.`,
		};
	return null;
}
